import { useAtom } from "jotai";
import { atomWithReset, RESET } from "jotai/utils";
import { useCallback, useEffect } from "react";
import useLockBodyScroll from "../useLockBodyScroll";

export type ModalKey =
  | "global"
  | "cart"
  | "sizeguide"
  | "notify"
  | "location"
  | "filter"
  | "sort"
  | "search"
  | "country-selector"
  | "mobile-country-selector";

const createModalAtom = (id: ModalKey) =>
  atomWithReset<{
    id: ModalKey;
    isOpen: boolean;
  }>({
    isOpen: false,
    id,
  });

const modalAtoms = new Map<ModalKey, ReturnType<typeof createModalAtom>>();

const getModalAtom = (id: ModalKey) => {
  if (!modalAtoms.has(id)) {
    modalAtoms.set(id, createModalAtom(id));
  }
  return modalAtoms.get(id)!;
};

export interface UseModalConfig {
  lock?: boolean;
  closeOnEscape?: boolean;
}

export const useModal = (id: ModalKey, config: UseModalConfig = {}) => {
  const { lock = false, closeOnEscape = true } = config;
  const { unlockBodyScroll, lockBodyScroll } = useLockBodyScroll(lock);

  const modalAtom = getModalAtom(id);
  const [modal, setModal] = useAtom(modalAtom);

  const openModal = useCallback(() => {
    if (lock) {
      lockBodyScroll();
    }
    setModal((prev) => ({
      ...prev,
      isOpen: true,
    }));
  }, [lock, lockBodyScroll, setModal]);

  const closeModal = useCallback(() => {
    if (lock) {
      unlockBodyScroll();
    }
    setModal((prev) => ({
      ...prev,
      isOpen: false,
    }));
  }, [lock, unlockBodyScroll, setModal]);

  const toggleModal = useCallback(() => {
    setModal((prev) => {
      const nextIsOpen = !prev.isOpen;

      if (lock) {
        if (nextIsOpen) {
          lockBodyScroll();
        } else {
          unlockBodyScroll();
        }
      }

      return {
        ...prev,
        isOpen: nextIsOpen,
      };
    });
  }, [setModal, lock, lockBodyScroll, unlockBodyScroll]);

  const setOpen = useCallback(
    (open: boolean) => {
      if (open) {
        openModal();
      } else {
        closeModal();
      }
    },
    [openModal, closeModal]
  );

  const resetModal = useCallback(() => {
    if (lock) {
      unlockBodyScroll();
    }
    setModal(RESET);
  }, [lock, unlockBodyScroll, setModal]);

  useEffect(() => {
    const controller = new AbortController();

    if (!closeOnEscape || !modal.isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeModal();
      }
    };

    document.addEventListener("keydown", handleEscape, {
      signal: controller.signal,
    });

    return () => {
      controller.abort();
    };
  }, [closeOnEscape, modal.isOpen, closeModal]);

  useEffect(() => {
    return () => {
      if (modal.isOpen && lock) {
        unlockBodyScroll();
      }
    };
  }, [modal.isOpen, lock, unlockBodyScroll]);

  return {
    isOpen: modal.isOpen,
    openModal,
    closeModal,
    toggleModal,
    setOpen,
    resetModal,
    id: modal.id,
  } as const;
};
